import type { ContentLockedRoute } from '~/composables/useContentLockedRoute';
import type { Capability } from '~/graphql/generated/graphql';

import { isContentAdminRoute, resetCrossAreaDenialNotices } from '~/composables/useContentLockedRoute';

/**
 * Admin routes that predate the capability system and still admit any admin.
 * Each area PR moves its pages onto a `requiredCapabilities` meta and deletes
 * their entries here, so the list only ever shrinks. A /content route in
 * neither place is denied — silence is never access for a new page.
 */
const LEGACY_UNGATED_ROUTES = [
  'content-index',
];

// The admin login is the way back in when a session lapses, and the catch-all
// renders a 404 that exposes nothing, so neither can be capability-gated.
const UNGATEABLE_ROUTES = ['content-login', 'content-slug'];

/** The destination as the gate reads it — a route name is a symbol on an unnamed record. */
interface GatedRoute {
  path: string;
  name?: unknown;
  meta: { requiredCapabilities?: Capability[] };
}

/** The lock the destination earns, or null when the viewer may have it. */
const lockFor = (to: GatedRoute): ContentLockedRoute | null => {
  if (!isContentAdminRoute(to.path)) {
    return null;
  }

  const routeName = String(to.name ?? '');
  if (UNGATEABLE_ROUTES.includes(routeName)) {
    return null;
  }

  const required = to.meta.requiredCapabilities;

  if (!required) {
    return LEGACY_UNGATED_ROUTES.includes(routeName) ? null : { path: to.path, missing: [] };
  }

  const { can, canAll } = useCapabilities();
  if (canAll(required)) {
    return null;
  }

  return { path: to.path, missing: required.filter(capability => !can(capability)) };
};

/**
 * Capability route protection (CP-10976): the admin surface default-denies, so
 * a /content page is only reachable with the capabilities its meta declares.
 * The backend directives are the enforcement; this is the UX half.
 *
 * A denial does not redirect. It records the route on shared state and lets the
 * default layout render the locked takeover in place of the page, so the page
 * component never mounts and a deep link behaves exactly like a nav click.
 *
 * The verdict is published as *pending* rather than committed: this guard runs while the browser
 * is still on the route being left, so committing here would take the takeover down a beat early
 * and let the locked page mount against its own path. `00.capabilityLockCommit` promotes the
 * verdict once the router has moved to the path it names.
 *
 * `to.path` arrives with the cluster prefix already stripped — Caddy removes it
 * and Vue Router's base handles the rest (see app/router.options.ts).
 */
export default defineNuxtRouteMiddleware((to) => {
  // Keyed by the destination: a navigation the router later cancels must not have its verdict
  // committed, and must not have the verdict of the navigation that replaced it committed for it.
  usePendingContentLockedRoute().value = { path: to.path, lock: lockFor(to) };
  // The cross-area toast dedupes per page visit, and this is the one hook that runs on every
  // navigation — so a return to a page the viewer already saw gets told about the denial again.
  resetCrossAreaDenialNotices();
});
